"use client";

import * as React from "react";
import { Box, Typography } from "@mui/material";
import styles from "./pyramid.module.css";

export type PyramidItem = {
  icon: string;
  label: string;
  body: string;
};

export type PyramidProps = {
  items: PyramidItem[];
  maxWidth?: number | string;
  colors?: { stroke?: string[]; fill?: string[]; line?: string[] };
  strokeWidth?: number;
  paddingTop?: number;
  paddingBottom?: number;
  baseHalfWidth?: number;
  leftColWidth?: string;
  rowHeight?: string;
  indentStep?: number;
  startGap?: number;
};

export function Pyramid({ items,
                          maxWidth = "min(92vw, 1100px)",
                          colors = {
                            stroke: ["#FFD54F", "#FF6F61", "#8EC5FF"],
                            fill:   ["#F6D66A33", "#FF6F6133", "#8EC5FF33"],
                            line:   ["#FFD54F", "#FF6F61", "#8EC5FF"],
                          },
                          strokeWidth = 3,
                          paddingTop = 0,
                          paddingBottom = 0,
                          baseHalfWidth = 50,
                          rowHeight = "clamp(45px, 11vw, 100px)",
                          indentStep = 50,
                          startGap = 0,
                        }: PyramidProps) {
  const levels = Math.max(1, items.length);
  const apexY = paddingTop;
  const baseY = 100 - paddingBottom;
  const H = baseY - apexY;
  const cx = 50;

  const edgeX = (y: number) => {
    const t = (y - apexY) / H;
    const half = Math.max(0, Math.min(50, t * baseHalfWidth));
    return { xl: cx - half, xr: cx + half };
  };

  const yTop = (i: number) => apexY + (H / levels) * i;
  const yBot = (i: number) => apexY + (H / levels) * (i + 1);
  const pick = (arr: string[] | undefined, i: number, fb: string) =>
      arr && arr.length ? arr[i % arr.length] : fb;

  return (
      <Box sx={{ maxWidth, mx: "auto", display: "grid", gap: 0 }}>
        {items.map((it, i) => (
            <Row
                key={i}
                index={i}
                levels={levels}
                icon={it.icon}
                label={it.label}
                body={it.body}
                apexY={apexY}
                yTop={yTop(i)}
                yBot={yBot(i)}
                edgeX={edgeX}
                rowHeight={rowHeight}
                strokeColor={pick(colors.stroke, i, "#fff")}
                fillColor={pick(colors.fill, i, "transparent")}
                lineColor={pick(colors.line, i, "#fff")}
                strokeWidth={strokeWidth}
                indentStep={indentStep}
                startGap={startGap}
            />
        ))}
      </Box>
  );
}

function Row(props: {
  index: number; levels: number;
  icon: React.ReactNode; label: string; body?: string;
  apexY: number; yTop: number; yBot: number;
  edgeX: (y: number) => { xl: number; xr: number };
  strokeColor: string; fillColor: string; lineColor: string;
  strokeWidth: number; rowHeight: string;
  indentStep: number; startGap: number;
}) {
  const {
    index, levels, icon, label, body,
    apexY, yTop, yBot, edgeX,
    strokeColor, fillColor, lineColor, strokeWidth,
    rowHeight, indentStep, startGap,
  } = props;

  const cx = 50;
  const { xl: xLt, xr: xRt } = edgeX(yTop);
  const { xl: xLb, xr: xRb } = edgeX(yBot);
  const isTop = index === 0;

  const points = isTop
      ? `${cx},${apexY} ${xRb},${yBot} ${xLb},${yBot}`
      : `${xLt},${yTop} ${xRt},${yTop} ${xRb},${yBot} ${xLb},${yBot}`;

  const viewBox = `${xLb} ${yTop} ${xRb - xLb} ${yBot - yTop}`;
  const isEmoji = typeof icon === "string";
  const sliceH = yBot - yTop;
  const emojiSize = Math.max(10, sliceH * 0.28);

  // Automatischer Einzug der Textspalte – statt hartem marginLeft 120/60/0
  const indentPx = startGap + indentStep * (levels - 1 - index);

  return (
      <Box
          sx={{
            display: "flex",
            py: 0.5,
          }}
      >
        <Box className={styles.mobile} sx={{pt: 2.5}}>
          <div style={{display: "flex", alignItems: "center",width: "56px", height: "56px", borderRadius: "50%", borderColor: lineColor, borderWidth: "3px", borderStyle: "solid"}}>
            <div style={{margin: "auto", fontSize: "35px"}}>
              {icon as string}
            </div>
          </div>
        </Box>

        {/* Linke Spalte: SVG */}
        <Box className={styles.desktop} sx={{ alignSelf: "end", position: "relative", height: rowHeight, ml: `${indentPx}px` }}>
          <svg
              viewBox={viewBox}
              preserveAspectRatio="xMaxYMax meet"
              width="100%"
              height="100%"
              style={{ display: "block" }}
              aria-hidden
          >
            <polygon
                points={points}
                fill={fillColor}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            {isEmoji && (
                <text
                    x={cx}
                    y={yTop + sliceH * 0.62}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={emojiSize}
                >
                  {icon as string}
                </text>
            )}
          </svg>

          {!isEmoji && (
              <Box
                  sx={{
                    position: "absolute", inset: 0,
                    display: "grid", placeItems: "center",
                    pointerEvents: "none", fontSize: { xs: 22, md: 28 }, lineHeight: 1,
                  }}
              >
                {icon}
              </Box>
          )}
        </Box>

        {/* Rechte Spalte: Linie UNTER dem Text + auto Einzug */}
        <Box className={styles.test}
            sx={{
              p: 2.5,
              borderBottom: "3px solid",
              borderBottomColor: lineColor,
              minWidth: 0,
              textAlign: "left",
              flexGrow: 1
            }}
        >
          <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
            {label}
          </Typography>
          {body && (
              <Typography variant="body1" sx={{ opacity: 0.92 }}>
                {body}
              </Typography>
          )}
        </Box>
      </Box>
  );
}
